import React, { useState } from 'react';
import { Box, Typography, Paper, TextField, Button, Container, IconButton, Fade, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from './context/ThemeContext';
import { BrandLogo } from './components/BrandLogo';

export const Feedback = () => {
  const navigate = useNavigate();
  const { mode } = useThemeContext();
  const isLight = mode === 'light';
  const [formData, setFormData] = useState({
    email: '',
    category: 'Course Content',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.email && formData.message) {
      setIsSubmitted(true);
      console.log('Feedback submitted:', formData);
      // In a real app, this would be an API call
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative Elements */}
      <Box sx={{
        position: 'absolute',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        bgcolor: 'rgba(255,255,255,0.05)',
        top: '-150px',
        right: '5%',
        zIndex: 0
      }} />
      <Box sx={{
        position: 'absolute',
        width: '200px',
        height: '200px',
        borderRadius: '40px',
        border: '30px solid rgba(255,255,255,0.08)',
        bottom: '5%',
        left: '10%',
        transform: 'rotate(15deg)',
        zIndex: 0
      }} />

      {/* Left Side: Context */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: { xs: 4, md: 10 },
        py: { xs: 8, md: 0 },
        zIndex: 1,
        color: '#fff'
      }}>
        <Box sx={{ mb: 6, cursor: 'pointer', display: 'inline-block' }} onClick={() => navigate('/interview-prep')}>
          <BrandLogo size="large" isLight={false} />
        </Box>
        
        <Typography variant="h1" sx={{ 
          fontSize: { xs: '2.5rem', md: '4.5rem' }, 
          fontWeight: 900, 
          lineHeight: 1, 
          mb: 2,
          letterSpacing: '-2px'
        }}>
          Give us your Feedback
        </Typography>
        <Typography variant="h3" sx={{ 
          fontSize: { xs: '1.5rem', md: '2.5rem' }, 
          fontWeight: 700, 
          mb: 3,
          opacity: 0.9 
        }}>
          Help us build the future
        </Typography>
        <Typography sx={{ 
          fontSize: '1.1rem', 
          maxWidth: 500, 
          opacity: 0.8,
          lineHeight: 1.6,
          fontWeight: 500
        }}>
          Your insights help us refine our interactive visualizations and tutoring. We read every single comment.
        </Typography>
      </Box>

      {/* Right Side: Feedback Form Card */}
      <Box sx={{
        flex: { md: '0 0 600px' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        zIndex: 1,
        background: { xs: 'transparent', md: 'rgba(255,255,255,0.05)' },
        backdropFilter: { md: 'blur(10px)' }
      }}>
        <Paper elevation={24} sx={{
          p: { xs: 4, md: 6 },
          width: '100%',
          maxWidth: 500,
          borderRadius: 6,
          bgcolor: isLight ? 'rgba(255, 255, 255, 0.95)' : '#111113',
          textAlign: 'center',
          boxShadow: '0 40px 100px -20px rgba(0,0,0,0.5)'
        }}>
          {!isSubmitted ? (
            <Fade in={!isSubmitted}>
              <Box component="form" onSubmit={handleSubmit} sx={{ textAlign: 'left' }}>
                <Typography variant="h4" fontWeight="800" sx={{ color: isLight ? '#0f172a' : '#fff', mb: 1, textAlign: 'center' }}>
                  Share Thoughts
                </Typography>
                <Typography variant="body1" sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 4, textAlign: 'center' }}>
                  We value your honest opinion on the FlowState experience.
                </Typography>

                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  required
                  variant="outlined"
                  value={formData.email}
                  onChange={handleChange}
                  sx={{ mb: 3 }}
                  InputProps={{
                    sx: { borderRadius: 3, bgcolor: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)' }
                  }}
                />

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="category-label">Feedback Category</InputLabel>
                  <Select
                    labelId="category-label"
                    name="category"
                    value={formData.category}
                    label="Feedback Category"
                    onChange={handleChange}
                    sx={{ borderRadius: 3, bgcolor: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)' }}
                  >
                    <MenuItem value="Course Content">Course Content</MenuItem>
                    <MenuItem value="Platform Features">Platform Features</MenuItem>
                    <MenuItem value="Mentorship">Tutoring</MenuItem>
                    <MenuItem value="Bug Report">Bug Report</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="How can we improve?"
                  name="message"
                  required
                  multiline
                  rows={4}
                  variant="outlined"
                  value={formData.message}
                  onChange={handleChange}
                  sx={{ mb: 4 }}
                  InputProps={{
                    sx: { borderRadius: 3, bgcolor: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)' }
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={!formData.email || !formData.message}
                  sx={{
                    py: 2,
                    borderRadius: 3,
                    fontSize: '1rem',
                    fontWeight: 800,
                    textTransform: 'none',
                    bgcolor: '#6366f1',
                    '&:hover': { bgcolor: '#4f46e5' },
                    boxShadow: '0 10px 25px -5px rgba(99, 102, 241, 0.4)'
                  }}
                >
                  Submit Feedback
                </Button>
              </Box>
            </Fade>
          ) : (
            <Fade in={isSubmitted}>
              <Box sx={{ py: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 80, color: '#10b981', mb: 3 }} />
                <Typography variant="h4" fontWeight="800" sx={{ color: isLight ? '#0f172a' : '#fff', mb: 2 }}>
                  Thank You!
                </Typography>
                <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 4 }}>
                  Your feedback has been received. We appreciate you taking the time to help us improve FlowState.
                </Typography>
                <Button 
                  onClick={() => navigate('/interview-prep')}
                  fullWidth
                  variant="outlined"
                  sx={{ py: 1.5, borderRadius: 3, fontWeight: 700 }}
                >
                  Back to Dashboard
                </Button>
              </Box>
            </Fade>
          )}
        </Paper>
      </Box>

      {/* Back Button */}
      <IconButton 
        onClick={() => navigate('/interview-prep')}
        sx={{ 
          position: 'absolute', 
          top: 20, 
          right: 20, 
          color: '#fff',
          bgcolor: 'rgba(255,255,255,0.1)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
        }}
      >
        <ArrowBackIcon />
      </IconButton>
    </Box>
  );
};
